import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';
import AppleCarousel from './AppleCarousel';
import './FoodHistory.css';

function FoodHistory({ userId }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all', 'safe', 'caution', 'unsafe'

  const loadHistory = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      return;
    }
    try {
      const data = await api.getFoodHistory(userId);
      setHistory(data);
    } catch (error) {
      console.error('Error loading history:', error);
      setHistory([]);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadHistory();
    const interval = setInterval(loadHistory, 30000);
    return () => clearInterval(interval);
  }, [loadHistory]);

  useEffect(() => {
    const handleHistoryUpdated = (event) => {
      const updatedUserId = event.detail?.userId;
      if (!updatedUserId || updatedUserId === String(userId)) {
        loadHistory();
      }
    };
    window.addEventListener('food-history-updated', handleHistoryUpdated);
    return () => window.removeEventListener('food-history-updated', handleHistoryUpdated);
  }, [userId, loadHistory]);

  const getStatusConfig = (status) => {
    const configs = {
      SAFE: { 
        icon: '✅', 
        color: 'success', 
        bg: 'var(--success-light)',
        border: 'var(--success-color)',
        label: 'Safe'
      },
      LIMITED: { 
        icon: '⚠️', 
        color: 'warning', 
        bg: 'var(--warning-light)',
        border: 'var(--warning-color)',
        label: 'Use Caution'
      },
      UNSAFE: { 
        icon: '❌', 
        color: 'error', 
        bg: 'var(--error-light)',
        border: 'var(--error-color)',
        label: 'Unsafe'
      },
      UNKNOWN: { 
        icon: '❓', 
        color: 'info', 
        bg: 'var(--info-light)',
        border: 'var(--info-color)',
        label: 'Unknown'
      }
    };
    return configs[status] || configs.UNKNOWN;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  const filteredHistory = filter === 'all' 
    ? history 
    : history.filter(item => {
        const status = item.safetyStatus?.toUpperCase();
        if (filter === 'safe') return status === 'SAFE';
        if (filter === 'caution') return status === 'LIMITED';
        if (filter === 'unsafe') return status === 'UNSAFE';
        return true;
      });

  if (loading) {
    return (
      <div className="food-history-modern">
        <div className="history-loading">
          <div className="modern-spinner"></div>
          <p>Loading your food history...</p>
        </div>
      </div>
    );
  }

  if (history.length === 0) {
    return (
      <div className="food-history-modern">
        <div className="history-empty-state">
          <div className="empty-icon">📜</div>
          <h3 className="empty-title">No food checks yet</h3>
          <p className="empty-description">
            Start checking foods to see your safety history here. Every check is logged and helps you make informed decisions.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="food-history-modern">
      {/* Filter Buttons */}
      <div className="history-filters">
        <button
          className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          All ({history.length})
        </button>
        <button
          className={`filter-btn ${filter === 'safe' ? 'active' : ''}`}
          onClick={() => setFilter('safe')}
        >
          ✅ Safe ({history.filter(h => h.safetyStatus === 'SAFE').length})
        </button>
        <button
          className={`filter-btn ${filter === 'caution' ? 'active' : ''}`}
          onClick={() => setFilter('caution')}
        >
          ⚠️ Caution ({history.filter(h => h.safetyStatus === 'LIMITED').length})
        </button>
        <button
          className={`filter-btn ${filter === 'unsafe' ? 'active' : ''}`}
          onClick={() => setFilter('unsafe')}
        >
          ❌ Unsafe ({history.filter(h => h.safetyStatus === 'UNSAFE').length})
        </button>
      </div>

      {/* History Timeline */}
      {filteredHistory.length === 0 ? (
        <div className="history-empty-filtered">
          <div className="empty-icon">🔍</div>
          <p>No foods found with this filter.</p>
        </div>
      ) : (
        <div className="history-timeline">
          {filteredHistory.length > 3 ? (
            <AppleCarousel>
              {filteredHistory.map((item) => {
                const config = getStatusConfig(item.safetyStatus);
                return (
                  <div key={item.id} className="history-item-modern">
                    <div 
                      className="history-item-status"
                      style={{ 
                        background: config.bg,
                        borderColor: config.border
                      }}
                    >
                      <div className="status-icon-large">{config.icon}</div>
                    </div>
                    <div className="history-item-content">
                      <div className="item-header">
                        <h4 className="item-food-name">{item.foodName}</h4>
                        <span 
                          className="item-status-badge"
                          style={{ 
                            background: config.bg,
                            color: config.border
                          }}
                        >
                          {config.label}
                        </span>
                      </div>
                      {item.message && (
                        <p className="item-message">{item.message}</p>
                      )}
                      <div className="item-meta">
                        <span className="item-time">{formatDate(item.checkedAt)}</span>
                        {item.culture && (
                          <span className="item-culture">• {item.culture}</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </AppleCarousel>
          ) : (
            <div className="history-list">
              {filteredHistory.map((item) => {
                const config = getStatusConfig(item.safetyStatus);
                return (
                  <div key={item.id} className="history-item-modern">
                    <div 
                      className="history-item-status"
                      style={{ 
                        background: config.bg,
                        borderColor: config.border
                      }}
                    >
                      <div className="status-icon-large">{config.icon}</div>
                    </div>
                    <div className="history-item-content">
                      <div className="item-header">
                        <h4 className="item-food-name">{item.foodName}</h4>
                        <span 
                          className="item-status-badge"
                          style={{ 
                            background: config.bg,
                            color: config.border
                          }}
                        >
                          {config.label}
                        </span>
                      </div>
                      {item.message && (
                        <p className="item-message">{item.message}</p>
                      )}
                      <div className="item-meta">
                        <span className="item-time">{formatDate(item.checkedAt)}</span>
                        {item.culture && (
                          <span className="item-culture">• {item.culture}</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default FoodHistory;
